import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ApolloError } from '@apollo/client';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import CardActionArea from '@mui/material/CardActionArea';
import Dialog from '@mui/material/Dialog';
import Helmet from '../../../components/Helmet';
import PlanLayout from '../../../layout/Plan';
import { usePlanQuery } from '../../../../graphql/query/plan/usePlanQuery';
import SubscribeCompanyPremiumAdvertisement from '../../../adminComponents/_advertisement/InhouseAdvertisements';
import CircularProgress from '../../../components/_loading/CircularProgress';
import Context from '../../../context';
import { useCreatePlanMutation } from '../../../../graphql/mutation/plan/createPlanMutation';
import useNotification from '../../../components/_hook/useNotification';
import ButtonSubmit from '../../../components/_button/ButtonSubmit';
import useRoutes from '../../../components/_hook/useRoutes';
import { useUnpublishPlansMutation } from '../../../../graphql/mutation/plan/unpublishPlansMutation';
import FormUpdatePlanPackage from '../_module/FormUpdatePlanPackage';
import SuperAdminPermission from '../../../adminComponents/_permission/SuperAdminPermission';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: '#404040',
    color: 'white',
  },
  premiumStatusText: {
    color: '#ff1744',
  },
  freeAccessLevelText: {
    color: '#ff1744',
  },
  freeStatusText: {
    color: theme.palette.primary.main,
  },
  premiumStatusButton: {
    color: 'white',
    backgroundColor: '#ff1744',
    '&:hover': {
      backgroundColor: '#d21439',
    },
  },
  freeStatusButton: {
    color: 'white',
    backgroundColor: theme.palette.primary.main,
  },
  summaryCard: {
    height: '100%',
  },
  summaryCardActionArea: {
    height: '100%',
  },
}));

function PlanDashboard() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { company } = useContext(Context);
  const { path } = useRoutes();
  const { isSuperAdmin } = useContext(Context);
  const [
    isOpenUpdatePlanPackageDialog,
    setIsOpenUpdatePlanPackageDialog,
  ] = useState(false);
  const planFragment = `_id
            status
            company
            users {
              id
            }
            access_levels {
              id
            }`;
  const { data, loading, refetch } = usePlanQuery(planFragment, {
    variables: { filter: { companyId: company._id as string } },
  });

  function onCompleted() {
    notification.success(t('success'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    createPlanMutation,
    { loading: isCreatingPlan },
  ] = useCreatePlanMutation(
    `createdPlan {
				${planFragment}
			}`,
    {
      onCompleted: () => {
        onCompleted();
        refetch();
      },
      onError,
    }
  );

  const [
    unpublishPlansMutation,
    { loading: isUnpublishingPlans },
  ] = useUnpublishPlansMutation(
    `updatedPlans {
				${planFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const plan = data ? data.plans.items[0] : null;

  if (loading) return <CircularProgress center color="primary" />;

  return (
    <>
      <Helmet title={`${t('dashboard')} | ${t('plan')}`} />
      {plan && (
        <Dialog
          open={isOpenUpdatePlanPackageDialog}
          onClose={() => setIsOpenUpdatePlanPackageDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <FormUpdatePlanPackage
            planId={plan._id}
            onSubmit={() => {
              setIsOpenUpdatePlanPackageDialog(false);
              refetch();
            }}
          />
        </Dialog>
      )}
      <Grid container spacing={2}>
        {plan && (
          <Grid item xs={12} container justifyContent="center" spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h4" align="center" gutterBottom>
                {t('summary')}
              </Typography>
            </Grid>
            <Grid item xs={12} container justifyContent="center" spacing={1}>
              <Grid item xs={12} md={3}>
                <Card className={classes.summaryCard}>
                  <CardContent>
                    <Typography variant="h6" align="center" gutterBottom>
                      {t('plans')}
                    </Typography>
                    <br />
                    {plan.status ? (
                      <Typography
                        variant="h4"
                        align="center"
                        className={classes.premiumStatusText}
                      >
                        {t('premium')}
                      </Typography>
                    ) : (
                      <Typography
                        variant="h4"
                        align="center"
                        className={classes.freeStatusText}
                      >
                        {t('free')}
                      </Typography>
                    )}
                  </CardContent>
                  <SuperAdminPermission>
                    <CardActions>
                      {plan.status ? (
                        <Button
                          fullWidth
                          variant="contained"
                          className={classes.premiumStatusButton}
                          onClick={() => {
                            unpublishPlansMutation({
                              variables: {
                                _ids: [plan._id],
                              },
                            });
                          }}
                        >
                          {t('deactivate')}
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          variant="contained"
                          fullWidth
                          onClick={() => setIsOpenUpdatePlanPackageDialog(true)}
                        >
                          {t('activate')}
                        </Button>
                      )}
                    </CardActions>
                  </SuperAdminPermission>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card className={classes.summaryCard}>
                  <CardActionArea
                    component={Link}
                    className={classes.summaryCardActionArea}
                    to={path('plan.administrators', {
                      companyID: company._id,
                    })}
                  >
                    <CardContent>
                      <Typography variant="h6" align="center" gutterBottom>
                        {t('administrator', {
                          count: plan.users ? plan.users.length : 0,
                        })}
                      </Typography>
                      <br />
                      <Typography variant="h4" align="center" color="primary">
                        {plan.users ? plan.users.length : 0}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card className={classes.summaryCard}>
                  {plan.status ? (
                    <CardActionArea
                      component={Link}
                      className={classes.summaryCardActionArea}
                      to={path('plan.access_level', {
                        companyID: company._id,
                      })}
                    >
                      <CardContent>
                        <Typography variant="h6" align="center" gutterBottom>
                          {t('access_level', {
                            count: plan.access_levels
                              ? plan.access_levels.length
                              : 0,
                          })}
                        </Typography>
                        <br />
                        <Typography variant="h4" align="center" color="primary">
                          {plan.access_levels ? plan.access_levels.length : 0}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  ) : (
                    <CardContent>
                      <Typography variant="h6" align="center" gutterBottom>
                        {t('access_level')}
                      </Typography>
                      <br />
                      <Typography
                        variant="h5"
                        align="center"
                        className={classes.freeAccessLevelText}
                      >
                        {t('premium_only')}
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Grid>
        )}

        {!plan && isSuperAdmin && (
          <SuperAdminPermission>
            <Grid item xs={12}>
              <Card variant="outlined" className={classes.root}>
                <CardContent>
                  <Typography variant="h5" align="center" gutterBottom>
                    {t('business_not_activated')}
                  </Typography>
                  <Typography variant="h6" align="center">
                    {t('activate_business_to_maximize_your_business_potential')}
                  </Typography>
                </CardContent>
                <CardActions>
                  <ButtonSubmit
                    size="large"
                    fullWidth
                    color="primary"
                    variant="contained"
                    isSubmitting={isCreatingPlan}
                    onClick={() => {
                      createPlanMutation({
                        variables: {
                          companyId: company._id,
                        },
                      });
                    }}
                  >
                    {t('activate')}
                  </ButtonSubmit>
                </CardActions>
              </Card>
            </Grid>
          </SuperAdminPermission>
        )}

        {(!plan || !plan.status) && (
          <Grid item xs={12}>
            <SubscribeCompanyPremiumAdvertisement />
          </Grid>
        )}
      </Grid>
    </>
  );
}

export default () => <PlanLayout main={<PlanDashboard />} />;
